// React and React libraries
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

// Chakra UI components
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';

// Third-party libraries
import DOMPurify from 'dompurify';
import { format, parseJSON } from 'date-fns';

// Internal components
import FormikFormControl from 'components/common/forms/FormikFormControl';
import NumberInput from 'components/common/forms/NumberInput';
import QuestionItemReadOnly from 'components/question/QuestionItemReadOnly';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';

// Store actions and selectors
import {
  getSubQuestProgressionSessionAction,
  updateQuizSessionAction,
} from 'store/modules/subQuestReport/subQuest-report-action';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';

// Utils and constants
import { getResultTextColor } from 'utils/columns/reports/sessionColumns';
import { ReducerStatus } from 'utils/constants';
import { ESSAY_ID } from 'utils/constants/question';

/**
 * Safely parses JSON string containing answers array
 * @param {string} answersJson - JSON string to parse
 * @returns {Array<string>} Parsed answers array or default empty string array
 */
const parseAnswersJson = (answersJson) => {
  try {
    return JSON.parse(answersJson || '[""]');
  } catch (error) {
    return [''];
  }
};

/**
 * Renders the scoring indicator (check/close icon) based on mark value
 * @param {number} mark - The question mark/score
 * @returns {JSX.Element} Avatar component with appropriate icon
 */
const renderScoreIndicator = (mark) => {
  const isCorrect = mark > 0;
  return (
    <Avatar
      size="2xs"
      bg={isCorrect ? 'green' : 'red'}
      icon={isCorrect ? <CheckIcon boxSize="2" /> : <CloseIcon boxSize="2" />}
      mr={2}
    />
  );
};

/**
 * Renders the score badge if mark is greater than 0
 * @param {number} mark - The question mark/score
 * @returns {JSX.Element|null} Badge component or null
 */
const renderScoreBadge = (mark) => {
  if (mark <= 0) return null;
  return <Badge colorScheme="green">+{parseFloat(mark.toFixed(2))}</Badge>;
};

/**
 * Renders session information display
 * @param {Object} session - The progression session data
 * @returns {JSX.Element} Session info component
 */
function SessionInfo({ session }) {
  return (
    <>
      <Flex>
        <Box flex={1}>
          <Text fontWeight="medium">Trainee:</Text>
          <Text>{session.ownerName}</Text>
        </Box>
        <Box flex={1}>
          <Text fontWeight="medium">Date Finished:</Text>
          <Text>
            {format(parseJSON(session.dateFinished), 'MMMM d, yyyy (h:mm a)')}
          </Text>
        </Box>
      </Flex>
      <Flex>
        <Box flex={1}>
          <Text fontWeight="medium">Result:</Text>
          <Text>
            <Badge colorScheme={getResultTextColor(session.result)}>
              {session.result}
            </Badge>
          </Text>
        </Box>
        <Box flex={1}>
          <Text fontWeight="medium">Score:</Text>
          <Text>{parseFloat(session.score.toFixed(2))}</Text>
        </Box>
      </Flex>
    </>
  );
}

SessionInfo.propTypes = {
  session: PropTypes.shape({
    ownerName: PropTypes.string.isRequired,
    dateFinished: PropTypes.string.isRequired,
    result: PropTypes.string.isRequired,
    score: PropTypes.number.isRequired,
  }).isRequired,
};

/**
 * Modal component for updating quiz session data, specifically for essay questions.
 * Allows editing marks and remarks for essay-type questions only.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|null} [props.classId=null] - Optional class ID
 * @returns {JSX.Element} The UpdateQuizSessionMd modal component
 */
function UpdateQuizSessionMd({ classId = null }) {
  const dispatch = useDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const progressionSession = useSelector(
    (state) => state.subQuestReport.progressionSession
  );

  const updateQuizSession = useSelector(
    (state) => state.subQuestReport.updateQuizSession
  );

  const getProgressionSession = useSelector(
    (state) => state.subQuestReport.getProgressionSession
  );

  const subQuest = useSelector((state) => state.subQuestReport.subQuest);

  /**
   * Handles opening/closing of the update modal
   * @param {boolean} isOpen - Whether the modal should be open
   */
  const handleToggleUpdateModal = (isOpen) => {
    dispatch(
      subQuestReportActions.setUpdateQuizSession({
        modal: isOpen,
      })
    );
  };

  /**
   * Handles the confirmation of the form submission
   */
  const handleConfirmSubmission = () => {
    if (pendingFormValues) {
      dispatch(
        updateQuizSessionAction({
          subQuestId: updateQuizSession.subQuestId,
          progressionId: updateQuizSession.progressionId,
          sessionId: updateQuizSession.sessionId,
          data: {
            essayQuestionnaires: pendingFormValues.questionnaires,
            classId,
          },
        })
      );
    }
  };

  /**
   * Handles the cancellation of the confirmation modal
   */
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  /**
   * Handles the result modal after update operation
   */
  const handleConfirmationResult = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);

    if (updateQuizSession.status === ReducerStatus.SUCCEEDED) {
      handleToggleUpdateModal(false);
      dispatch(subQuestReportActions.toggleProgressionSessionsModal(true));
    }

    // Reset the status and error to allow retry
    dispatch(
      subQuestReportActions.setUpdateQuizSession({
        status: ReducerStatus.IDLE,
        error: null,
      })
    );
  };

  useEffect(() => {
    if (
      updateQuizSession.modal &&
      updateQuizSession.subQuestId &&
      updateQuizSession.progressionId &&
      updateQuizSession.sessionId
    ) {
      dispatch(
        subQuestReportActions.toggleProgressionSessionsModal(
          !updateQuizSession.modal
        )
      );
      dispatch(
        getSubQuestProgressionSessionAction({
          subQuestId: updateQuizSession.subQuestId,
          progressionId: updateQuizSession.progressionId,
          sessionId: updateQuizSession.sessionId,
        })
      );
    }
  }, [
    dispatch,
    updateQuizSession.subQuestId,
    updateQuizSession.progressionId,
    updateQuizSession.sessionId,
    updateQuizSession.modal,
  ]);

  return (
    <>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleConfirmationResult}
        status={updateQuizSession.status}
        successMessage="Quiz session has been updated successfully!"
        description={`Are you sure you want to update this quiz session? This action will save the marks and remarks for essay questions.${
          subQuest?.subQuestQuiz?.showResult
            ? ' Note: The quiz is set to show results, so the student will be notified of the score changes.'
            : ''
        }`}
        error={updateQuizSession.error}
      />

      <Modal
        size="2xl"
        isOpen={updateQuizSession.modal}
        onClose={() => handleToggleUpdateModal(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Quiz Session</ModalHeader>
          <ModalCloseButton />
          <ModalBody my={3}>
            {getProgressionSession.status === ReducerStatus.LOADING &&
              !progressionSession && (
                <Alert my={4} status="loading" variant="left-accent">
                  <AlertIcon />
                  Fetching data please wait
                </Alert>
              )}

            {progressionSession && (
              <VStack align="stretch" gap={4} my={4}>
                <Alert status="info" variant="left-accent">
                  <AlertIcon />
                  You can only edit the mark and remark for essay questions.
                </Alert>
                <SessionInfo session={progressionSession} />

                <Divider />
                {/* Questionnaire Formik Form */}
                {Array.isArray(progressionSession.questionnaires) && (
                  <Formik
                    initialValues={{
                      questionnaires: progressionSession.questionnaires
                        .filter((q) => q.question.questionType.id === ESSAY_ID)
                        .map((q) => ({
                          id: q.id,
                          mark: q.mark,
                          remarks: q.remarks || '',
                        })),
                    }}
                    onSubmit={(values) => {
                      setPendingFormValues(values);
                      setShowConfirmation(true);
                    }}
                  >
                    {({ values, handleChange, handleBlur }) => (
                      <Form>
                        <VStack align="stretch" gap={4}>
                          {progressionSession.questionnaires.map(
                            (questionnaire, index) =>
                              questionnaire.question.questionType.id ===
                              ESSAY_ID ? (
                                <VStack
                                  align="stretch"
                                  gap={2}
                                  key={questionnaire.id}
                                >
                                  <Flex>
                                    <Text fontSize="xs" as="samp">
                                      Question {index + 1}
                                    </Text>

                                    <Spacer />
                                    {renderScoreIndicator(questionnaire.mark)}
                                    {renderScoreBadge(questionnaire.mark)}
                                  </Flex>

                                  <Box
                                    fontSize="md"
                                    fontWeight={500}
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(
                                        questionnaire.question.questionHtml
                                      ),
                                    }}
                                  />

                                  <FormikFormControl label="Answer:">
                                    <Text>
                                      {
                                        parseAnswersJson(
                                          questionnaire.answersJson
                                        )[0]
                                      }
                                    </Text>
                                  </FormikFormControl>

                                  {questionnaire.question.questionType.id ===
                                  ESSAY_ID ? (
                                    <>
                                      <NumberInput
                                        label="Mark"
                                        helperText={`Current Mark: ${questionnaire.question.mark} - Max: ${questionnaire.question.mark} (Partial Mark ${questionnaire.question.allowPartialPoints ? 'Allowed' : 'Not Allowed'})`}
                                        name={`questionnaires[${index}].mark`}
                                        value={
                                          values.questionnaires[index].mark ===
                                          ''
                                            ? ''
                                            : Number(
                                                values.questionnaires[index]
                                                  .mark
                                              )
                                        }
                                        onChange={(value) =>
                                          handleChange({
                                            target: {
                                              name: `questionnaires[${index}].mark`,
                                              value:
                                                value === ''
                                                  ? ''
                                                  : Number(value),
                                            },
                                          })
                                        }
                                        onBlur={handleBlur}
                                        min={0}
                                        max={questionnaire.question.mark}
                                        step={
                                          questionnaire.question
                                            .allowPartialPoints
                                            ? 0.01
                                            : 1
                                        }
                                        precision={
                                          questionnaire.question
                                            .allowPartialPoints
                                            ? 2
                                            : 0
                                        }
                                      />

                                      <FormControl mb={2}>
                                        <FormLabel>Remarks</FormLabel>
                                        <Textarea
                                          name={`questionnaires[${index}].remarks`}
                                          value={
                                            values.questionnaires[index].remarks
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                        />
                                      </FormControl>
                                    </>
                                  ) : (
                                    <>
                                      <Input
                                        value={questionnaire.mark}
                                        isReadOnly
                                        mb={2}
                                        type="number"
                                      />
                                      <Textarea
                                        value={questionnaire.remarks || ''}
                                        isReadOnly
                                        mb={2}
                                      />
                                    </>
                                  )}
                                </VStack>
                              ) : (
                                <QuestionItemReadOnly
                                  key={questionnaire.id}
                                  index={index}
                                  answersJson={questionnaire.answersJson}
                                  question={questionnaire.question}
                                  mark={questionnaire.mark}
                                  remarks={questionnaire.remarks}
                                />
                              )
                          )}

                          <Flex gap={3} justifyContent="flex-end">
                            {/* Close button */}
                            <Button
                              colorScheme="blue"
                              onClose={() => handleToggleUpdateModal(false)}
                            >
                              Close
                            </Button>
                            {/* Proceed button */}
                            <Button
                              type="submit"
                              variant="outline"
                              colorScheme="red"
                              isLoading={
                                updateQuizSession.status ===
                                ReducerStatus.LOADING
                              }
                              loadingText="Saving..."
                            >
                              Publish Changes
                            </Button>
                          </Flex>
                        </VStack>
                      </Form>
                    )}
                  </Formik>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

// PropTypes for better type checking
UpdateQuizSessionMd.propTypes = {
  classId: PropTypes.number,
};

UpdateQuizSessionMd.defaultProps = {
  classId: null,
};

export default UpdateQuizSessionMd;
